import { Injectable, inject } from '@angular/core';
import { CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

@Injectable({
	providedIn: 'root'
})
export class ActivityTaskEditModeGuard implements CanDeactivate<Observable<boolean> | boolean> {
	private activityEditModeService = inject(ActivityEditModeService);
	private featureFlag = inject(NgxFeatureFlagService);

	public canDeactivate (
		_component: unknown,
		_currentRoute: ActivatedRouteSnapshot,
		currentState: RouterStateSnapshot,
		nextState?: RouterStateSnapshot
	): Observable<boolean> | boolean {
		if (this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG')) {
			return true;
		}

		if (!this.activityEditModeService.isEditMode() || !this.activityEditModeService.hasUnsavedChanges()) {
			return true;
		}

		if (nextState && this.isNavigatingBetweenTaskRoutes(currentState, nextState)) {
			return true;
		}

		return this.activityEditModeService.confirmDiscardChanges();
	}

	private isNavigatingBetweenTaskRoutes (currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): boolean {
		const currentParams = this.extractRouteParams(currentState.root);
		const nextParams = this.extractRouteParams(nextState.root);

		// 'evaluate' is the Submissions route (Instructor Overview), not a task preview route
		// Don't treat it as a task route for seamless navigation purposes
		const currentIsTaskRoute = currentParams.activity_id && currentParams.type && currentParams.type !== 'evaluate';
		const nextIsTaskRoute = nextParams.activity_id && nextParams.type && nextParams.type !== 'evaluate';

		// Allow navigation if both are task routes for the same activity
		const isSameActivity = currentParams.activity_id === nextParams.activity_id;
		return currentIsTaskRoute && nextIsTaskRoute && isSameActivity;
	}

	private extractRouteParams (route: ActivatedRouteSnapshot) {
		let params: Record<string, any> = {};

		// collect all params from route tree
		let currentRoute: ActivatedRouteSnapshot | null = route;
		while (currentRoute) {
			params = { ...params, ...currentRoute.params };

			if (currentRoute.firstChild) {
				currentRoute = currentRoute.firstChild;
			} else {
				break;
			}
		}

		return params;
	}
}
